<template>
  <div class="ai-chat-surface">
    <div class="_header">
      <ai-chat-history-dropdown
        class="_title"
        :chat-id="chatId"
        @select="$emit('select-chat', $event)"
      />
      <div class="_actions">
        <m-tooltip placement="bottom">
          <template #title>
            {{ $t('aiChat.newChat') }}
          </template>
          <m-btn
            icon="plus"
            light
            hide-border
            fab
            small
            @click="$emit('new-chat')"
          />
        </m-tooltip>
        <ai-chat-chat-menu
          v-if="chatId !== null"
          :chat-id="chatId"
        />
        <slot name="header-actions" />
      </div>
    </div>
    <div class="_body">
      <ai-chat-message-list
        :messages="messages"
        :outgoing="outgoing"
        :pending="pending"
        :failure="failure"
        :loading="getChatLoading"
        :retryable="retryable"
        @retry="onRetry"
      />
      <ai-chat-composer
        :key="chatId"
        :pending="pending"
        :send="sendMessage"
        :stoppable="stoppable"
        :stop="onStop"
      />
    </div>
  </div>
</template>

<script setup>
import AiChatChatMenu from '@/components/ai-chat/AiChatChatMenu.vue';
import AiChatComposer from '@/components/ai-chat/AiChatComposer.vue';
import AiChatHistoryDropdown from '@/components/ai-chat/AiChatHistoryDropdown.vue';
import AiChatMessageList from '@/components/ai-chat/AiChatMessageList.vue';
import useAiChatConversation from '@/composables/ai-chat/ai-chat-conversation';
import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import useSnackBar from '@/composables/snackbar';
import { computed, toRef, watch } from 'vue';
import { logCatch } from '@/lib/logger/logger';

const props = defineProps({
  chatId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['new-chat', 'select-chat', 'chat-created', 'chat-deleted']);

const { queryChat, selectChat, getChatLoading } = useAiChatRepo();
const onChatCreated = (uid) => emit('chat-created', uid);
const {
  messages,
  outgoing,
  pending,
  failure,
  sendMessage,
  retryable,
  retry,
  stoppable,
  stop,
} = useAiChatConversation(toRef(props, 'chatId'), { onChatCreated });

const snackbar = useSnackBar();
const onRetry = () => retry().catch(logCatch());
const onStop = () => stop().catch(logCatch(() => snackbar.error()));

const activeChat = computed(() => (props.chatId === null ? null : selectChat(props.chatId)));

watch(() => props.chatId, (chatId) => {
  if (chatId !== null) {
    queryChat(chatId);
  }
}, { immediate: true });

// deleting the open chat drops it from the store before the menu that triggered the
// delete is unmounted, so the surface watches the chat itself rather than the event
watch(activeChat, (chat, previous) => {
  if (chat === undefined && previous?.uid === props.chatId) {
    emit('chat-deleted');
  }
});
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-surface {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: hidden;

    ._header {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: .4rem;
      padding: .8rem 1.2rem;
    }

    ._title {
      min-width: 0;
    }

    ._actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: .2rem;
      margin-left: auto;
    }

    ._body {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }
  }
</style>
